import { useQuery } from '@tanstack/react-query';

import { useMemo, useState } from 'react';

import { AddCircle, CheckCircle, ContentCopy, Delete, Edit, Refresh, Visibility } from '@mui/icons-material';
import { Button, Chip, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridFilterModel, GridSortModel } from '@mui/x-data-grid';

import { useApiClient, useIsMobile, usePersistedSetting, usePersistedTablePagination, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics, getResponsiveDataGridProps } from '@@components';

import { ServiceConnectionClient } from '../../api/api';
import { ServiceConnectionModel, ServiceConnectionModelPagingResultModel } from '../../api/api';
import IconComponent from '../../common/components/IconComponent';
import { PresenceIndicator } from '../../components/Presence/PresenceIndicator';
import { usePresence } from '../../components/Presence/usePresence';
import ServiceConnectionMetadataProvider from './Contexts/ServiceConnectionMetadataContext';
import CreateServiceConnectionDialog from './CreateServiceConnectionDialog';
import EditServiceConnectionDialog from './EditServiceConnectionDialog';
import useServiceConnectionMetadata from './Hooks/useServiceConnectionMetadata';
import ServiceConnectionStatusChip from './ServiceConnectionStatusChip';
import ServiceConnectionObsoleteIndicator from './ServiceConnectionObsoleteIndicator';

const pageSizeOptions = [20, 50];
const initialFilterModel = {
	items: [],
};
const initialSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }];

const sortFieldName: Record<string, string> = {
	name: 'Name',
	type: 'Type',
	category: 'Category',
	isPerEnvironment: 'IsPerEnvironment',
	statusName: 'Status',
};

function ServiceConnectionViewers({ connectionId }: { connectionId: string }) {
	const viewers = usePresence('service-connection', connectionId, 'definition', null, true);
	return <PresenceIndicator viewers={viewers} />;
}

function ServiceConnectionsListViewInner() {
	const client = useApiClient(ServiceConnectionClient);
	const toastService = useToastService();
	const { serviceConnectionByName } = useServiceConnectionMetadata();
	const isMobile = useIsMobile();

	const { paginationModel, setPaginationModel } = usePersistedTablePagination('serviceConnections.pageSize');
	const [sortModel, setSortModel] = usePersistedSetting<GridSortModel>('serviceConnections.sortModel', initialSortModel);

	const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);

	const { data, isLoading, refetch } = useQuery<ServiceConnectionModelPagingResultModel>({
		queryKey: ['serviceConnections', paginationModel, sortModel, filterModel],
		queryFn: async () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? (sortModel[0].sort === 'asc' ? true : false) : true;
			return client.search({ paging: { pageIndex: paginationModel.page, pageSize: paginationModel.pageSize, sortBy, sortAscending: sortDirection } });
		},
	});

	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedServiceConnection, setSelectedServiceConnection] = useState<ServiceConnectionModel | null>(null);

	const handleValidateConnection = async (serviceConnection: ServiceConnectionModel) => {
		try {
			toastService.info(`Validating connection: ${serviceConnection.name}...`);

			// Use the actual verification endpoint
			const result = await client.verify(serviceConnection.id);

			if (result.success) {
				toastService.success(`Connection "${serviceConnection.name}" is valid and working correctly.`);
			} else {
				const errorMessage = result.errorMessage || 'Connection validation failed';
				toastService.error(`Connection "${serviceConnection.name}" validation failed: ${errorMessage}`);
			}
		} catch (error) {
			console.error('Error validating connection:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to validate connection "${serviceConnection.name}": ${errorMessage}`);
		}
	};

	const handleEditConnection = (serviceConnection: ServiceConnectionModel) => {
		setSelectedServiceConnection(serviceConnection);
		setOpenEditDialog(true);
	};

	const handleDeleteConnection = (serviceConnection: ServiceConnectionModel) => {
		setSelectedServiceConnection(serviceConnection);
		setOpenDeleteDialog(true);
	};

	const handleConfirmDelete = async () => {
		if (!selectedServiceConnection) return;

		try {
			await client.delete(selectedServiceConnection.id);
			toastService.success(`Service Connection "${selectedServiceConnection.name}" deleted successfully.`);
			refetch();
		} catch (error) {
			console.error('Error deleting service connection:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete service connection: ${errorMessage}`);
		} finally {
			setOpenDeleteDialog(false);
			setSelectedServiceConnection(null);
		}
	};

	const handleCancelDelete = () => {
		setOpenDeleteDialog(false);
		setSelectedServiceConnection(null);
	};

	const handleCopyId = (serviceConnection: ServiceConnectionModel) => {
		navigator.clipboard.writeText(serviceConnection.id);
		toastService.success('ID copied to clipboard');
	};

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize : data.items.length) : 0),
		[paginationModel, data],
	);

	const columns: GridColDef<ServiceConnectionModel>[] = [
		{
			field: 'icon',
			headerName: '',
			sortable: false,
			width: 40,
			renderCell: (params) => {
				const meta = serviceConnectionByName.get(params.row.type);
				return meta?.iconUrl || meta?.iconName ? <IconComponent iconUrl={meta.iconUrl} iconName={meta.iconName} /> : null;
			},
		},
		{
			field: 'name',
			headerName: 'Name',
			sortable: true,
			flex: 4,
			renderCell: (params) => (
				<Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%', minWidth: 0 }}>
					<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={params.value}>{params.value}</span>
					<ServiceConnectionObsoleteIndicator categories={serviceConnectionByName.get(params.row.type)?.categories ?? params.row.categories} />
					<ServiceConnectionViewers connectionId={params.row.id} />
					{params.row.isReadOnly && <Chip label="System" color="info" variant="outlined" size="small" />}
				</Stack>
			),
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 2,
			sortable: true,
		},
		{
			field: 'category',
			headerName: 'Category',
			flex: 2,
			sortable: true,
		},
		{
			field: 'statusName',
			headerName: 'Status',
			flex: 1,
			sortable: true,
			renderCell: (params) => {
				if (!params.value) return null;
				return <ServiceConnectionStatusChip status={params.value} />;
			},
		},
		{
			field: 'enabledEnvironments',
			headerName: 'Enabled For',
			flex: 1,
			sortable: false,
			renderCell: (params) => {
				const envs = params.value as string[] | undefined;
				if (!envs || envs.length === 0) return 'All environments';
				return `${envs.length} environment${envs.length !== 1 ? 's' : ''}`;
			},
		},
		{
			field: 'isPerEnvironment',
			headerName: 'Per Environment',
			flex: 1,
			sortable: true,
			renderCell: (params) => (params.value ? 'Yes' : 'No'),
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: 120,
			getActions: (params) => [
				<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => handleCopyId(params.row)} showInMenu />,
				<GridActionsCellItem
					icon={params.row.isReadOnly ? <Visibility /> : <Edit />}
					label={params.row.isReadOnly ? 'View' : 'Edit'}
					onClick={() => handleEditConnection(params.row)}
					showInMenu
					disabled={params.row.isLocked}
				/>,
				<GridActionsCellItem icon={<CheckCircle />} label="Validate" onClick={() => handleValidateConnection(params.row)} showInMenu />,
				<GridActionsCellItem
					icon={<Delete />}
					label="Delete"
					onClick={() => handleDeleteConnection(params.row)}
					showInMenu
					disabled={params.row.isLocked || params.row.isReadOnly}
				/>,
			],
		},
	];

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader title="Service Connections" subtitle="Configure the external services used by your apps and flows." utilityActions={<Tooltip title="Refresh service connections"><IconButton onClick={() => refetch()}><Refresh /></IconButton></Tooltip>} primaryAction={<Button variant="contained" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)}>Create connection</Button>} statistics={<GalleryStatistics items={[{ label: 'Connections on this page', value: data?.items.length ?? 0 }]} />} />
			<Paper sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={data?.items ?? []}
					rowCount={rowCount}
					columns={columns}
					getRowId={(row) => row.id}
					paginationMode="server"
					sortingMode="server"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					filterModel={filterModel}
					onFilterModelChange={setFilterModel}
					columnVisibilityModel={{
						icon: !isMobile,
						type: !isMobile,
						category: !isMobile,
						enabledEnvironments: !isMobile,
						isPerEnvironment: !isMobile,
					}}
					{...getResponsiveDataGridProps({ isMobile })}
				/>
			</Paper>
			<CreateServiceConnectionDialog open={openCreateDialog} setOpen={setOpenCreateDialog} onSuccess={() => refetch()} />
			{selectedServiceConnection && (
				<EditServiceConnectionDialog
					open={openEditDialog}
					setOpen={setOpenEditDialog}
					serviceConnection={selectedServiceConnection}
					onSuccess={() => {
						refetch();
						setSelectedServiceConnection(null);
					}}
				/>
			)}
			<ConfirmDialog
				open={openDeleteDialog}
				title="Delete Service Connection"
				message={
					selectedServiceConnection
						? `Are you sure you want to delete "${selectedServiceConnection.name}"? This action cannot be undone and may affect any flows or operations that depend on it.`
						: ''
				}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>
		</Stack>
	);
}

export default function ServiceConnectionsListView() {
	return (
		<ServiceConnectionMetadataProvider>
			<ServiceConnectionsListViewInner />
		</ServiceConnectionMetadataProvider>
	);
}
